import type { BaseEntityState, AuditFields } from '@crm/shared';

/**
 * CALL — Entita' di engagement "chiamata" del CRM, agganciabile a
 * Person/Business tramite Relationship polimorfica. Schema allineato a
 * rest-openapi.yaml (componente `Call`/`CallInput`).
 *
 * Differenza dagli altri enum del progetto: `direction` viaggia come
 * STRING (`IN`/`OUT`) — schema yaml `direction: string` (no mapping
 * int<->string). `status` invece e' un int32 mappato a `CallStatus`
 * (vedi callMappers.ts).
 *
 * SYNC CROSS-LAYER — modifiche vanno replicate in parallelo su:
 *   CallDirection: modules/crm-util/.../CallDirection.java,
 *                  callMappers.ts (CALL_DIRECTIONS), locales/{it,en}.json
 *   CallStatus:    modules/crm-util/.../CallStatus.java,
 *                  callMappers.ts (CALL_STATUS_TO_INT), locales/{it,en}.json
 */
export type CallDirection = 'IN' | 'OUT';
export type CallStatus =
  | 'busy'
  | 'connected'
  | 'message'
  | 'no-response'
  | 'wrong-number';

// Array UI per Select (label = chiave i18n).
export const CALL_DIRECTIONS: { value: CallDirection; label: string }[] = [
  { value: 'IN', label: 'call.directions.in' },
  { value: 'OUT', label: 'call.directions.out' },
];

export const CALL_STATUSES: { value: CallStatus; label: string }[] = [
  { value: 'connected', label: 'call.statuses.connected' },
  { value: 'busy', label: 'call.statuses.busy' },
  { value: 'no-response', label: 'call.statuses.no-response' },
  { value: 'message', label: 'call.statuses.message' },
  { value: 'wrong-number', label: 'call.statuses.wrong-number' },
];

export interface Call extends AuditFields {
  callId: number;
  direction: CallDirection;
  callDate: string;
  description: string;
  // Azioni di follow-up emerse dalla chiamata. Facoltativo: il backend lo
  // popola da fuori (automazione/import); la UI lo mostra in modifica solo
  // se valorizzato (vedi CallEditFormBody). Non presente nel form di creazione.
  actionItems?: string;
  status: CallStatus;
  // Interno aziendale che ha partecipato alla chiamata, scritto dal centralino
  // sulle entranti. Il numero e' in sola lettura; il nome si puo' correggere a
  // mano quando il centralino non lo trasmette (vedi CallEditFormBody).
  internalExtension?: string;
  internalName?: string;
}

export interface CallState extends BaseEntityState<Call> {
  filters: {
    direction?: CallDirection;
    status?: CallStatus;
  };
  totalCount: number;
}

export interface CallCreatePayload {
  direction: CallDirection;
  callDate: string;
  description: string;
  actionItems?: string;
  status: CallStatus;
  internalName?: string;
}

export interface CallUpdatePayload extends Partial<CallCreatePayload> {
  callId: number;
}
